import clsx from 'clsx';
import React from 'react';
import { getWrappedElement } from '@react-spectrum/utils';
import styles from '@danske/sapphire-css/components/icon/icon.module.css';

import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export type IconProps = GlobalDomAttributes &
  SapphireStyleProps & {
    'aria-label'?: string;
    /**
     * The size of the icon.
     * @default 'md'
     */
    size?: 'sm' | 'md' | 'lg' | 'xl';
    /**
     * The semantic color of the icon
     * @default 'inherit'
     */
    color?:
      | 'inherit'
      | 'primary'
      | 'secondary'
      | 'positive'
      | 'negative'
      | 'warning'
      | 'informative';
    children: React.ReactNode;
  };

export const Icon = React.forwardRef(function Icon(
  {
    size = 'md',
    color = 'inherit',
    'aria-label': ariaLabel,
    ...props
  }: IconProps,
  ref: React.Ref<HTMLSpanElement>
) {
  useThemeCheck();

  const { styleProps } = useSapphireStyleProps(props);
  const colorClass =
    color === 'inherit' ? undefined : styles[`sapphire-icon--${color}`];

  return React.cloneElement(getWrappedElement(props.children), {
    ref,
    role: 'img',
    ...(ariaLabel ? { 'aria-label': ariaLabel } : { 'aria-hidden': true }),
    className: clsx(
      styles['sapphire-icon'],
      styleProps.className,
      styles[`sapphire-icon--${size}`],
      colorClass
    ),
    style: styleProps.style,
  });
});
